import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import AdClose from './assets/ad_close.svg'
import IconArrowLeft from '~/assets/images/arrow-double-left-gray.svg'
import IconArrowRight from '~/assets/images/arrow-double-right-white.svg'
import IconGift from '~/assets/images/gift.svg'
import { useBreakpoint, useIsOverflow } from '~/hooks'
import { useState, useRef, useEffect } from 'react'
import { useEvents, useGA } from '~/hooks'

const AdBanner = () => {
  const { t } = useTranslation()
  const { trackClick, trackView } = useGA()
  const [visible, setVisible] = useState(true)
  const [expand, setExpand] = useState(false)
  const { isMobile } = useBreakpoint()
  const descRef = useRef()
  const isOverflow = useIsOverflow(descRef)
  const [latestEvents] = useEvents()

  useEffect(() => {
    if (isMobile && latestEvents && visible)
      return trackView('webtl_products_banner')
  }, [])

  if (!latestEvents || !visible) return null

  const { title, image, url } = latestEvents

  const jumpToAd = () => {
    trackClick('webtl_products_banner_detail', { title, url })
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleGiftClick = () => {
    trackView('webtl_products_banner')
    setExpand(true)
  }

  if (isMobile) {
    return (
      <StyledAdBannerMobile isOverflow={isOverflow}>
        <div className="ad">
          <div className="content" onClick={jumpToAd}>
            <span className="label">{t('ad_banner_label')}</span>
            <span className="scroll-container">
              <span ref={descRef} className="desc">
                {title}
              </span>
            </span>
          </div>
          <span className="close" onClick={() => setVisible(false)}>
            <img src={AdClose} alt="close" />
          </span>
        </div>
      </StyledAdBannerMobile>
    )
  }

  return (
    <StyledAdBannerPC>
      <div className="ad">
        {expand ? (
          <>
            <span className="collapse" onClick={() => setExpand(false)}>
              <img src={IconArrowRight} alt="collapse" />
            </span>
            <img src={image} onClick={jumpToAd} alt="banner" />
          </>
        ) : (
          <StyledAdBannerPCCollapse onClick={handleGiftClick}>
            <StyledPCIconGift>
              <img src={IconGift} />
            </StyledPCIconGift>
            <StyledPCIconExpand>
              <img src={IconArrowLeft} />
            </StyledPCIconExpand>
          </StyledAdBannerPCCollapse>
        )}
      </div>
    </StyledAdBannerPC>
  )
}

const StyledAdBannerPC = styled.div`
  position: fixed;
  top: 179px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  z-index: 1;

  .ad {
    position: relative;
  }

  .collapse {
    position: absolute;
    right: 8px;
    top: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      display: inline-block;
      border-radius: 50%;
      padding: 5px;
      &:hover {
        background: rgba(217, 217, 217, 0.5);
      }
    }
  }

  img {
    width: 310px;
    border-radius: 16px;
    cursor: pointer;
  }
`

const StyledAdBannerPCCollapse = styled.div`
  display: flex;
  align-items: center;
  padding: 6px;
  width: 66px;
  height: 44px;
  background: #ffffff;
  border-radius: 22px 0 0 22px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05), 0px 2px 10px rgba(0, 0, 0, 0.02);
`

const StyledPCIconGift = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fafafd;

  img {
    width: 18px;
    height: 18px;
  }
`

const StyledPCIconExpand = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 9px;
  height: 8px;
  margin-left: 6px;

  img {
    width: 9px;
    height: 8px;
  }
`

const StyledAdBannerMobile = styled.div<{ isOverflow: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: env(safe-area-inset-bottom);
  user-select: none;
  height: 34px;
  z-index: 10;
  background: linear-gradient(100.64deg, #e8e8ff 8.06%, #fbe0ff 90.79%);

  .ad {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;

    .content {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      height: 100%;

      .label {
        align-self: flex-start;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 8px;
        padding: 3px 6px;
        border-bottom-right-radius: 9px;
        background: #bcaffc;

        font-weight: 400;
        font-size: 11px;
        line-height: 11px;
        color: #ffffff;
      }

      .scroll-container {
        display: inline-block;
        max-width: 299px;
        height: 25px;
        overflow: hidden;

        .desc {
          display: inline-block;
          width: 100%;
          font-weight: 300;
          font-size: 12px;
          line-height: 25px;
          color: #000000;
          white-space: nowrap;

          ${({ isOverflow }) =>
            isOverflow &&
            css`
              animation: textscroll 8s linear infinite;
            `}
        }

        @keyframes textscroll {
          0% {
            -moz-transform: translateX(0);
            -webkit-transform: translateX(0);
            transform: translateX(0);
          }
          13% {
            -moz-transform: translateX(0);
            -webkit-transform: translateX(0);
            transform: translateX(0);
          }
          87% {
            -moz-transform: translateX(-100%);
            -webkit-transform: translateX(-100%);
            transform: translateX(-100%);
          }
          100% {
            -moz-transform: translateX(-100%);
            -webkit-transform: translateX(-100%);
            transform: translateX(-100%);
          }
        }
      }
    }

    .close {
      width: 18px;
      height: 18px;
      margin-right: 8px;
      cursor: pointer;
      z-index: 1;
      backdrop-filter: blur(6px);
      background: rgba(20, 20, 72, 0.3);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        display: inline-block;
        width: 8px;
        height: 8px;
      }
    }
  }
`

export default AdBanner
